import store from "app/main/store";
import Moment from 'moment/moment';
import { IFilterParameters } from "./types";
import { IVenueBookings, IVenueScheduleDetailsByTheDate, IVenueTablesLayoutsForTheDay, saveBookingsByTheDate, saveFilteredBookings, saveScheduleByTheDate, saveTablesByTheDate } from 'app/reducers/venueSlice';
import { ReportingService } from "app/services/reporting/reporting.service";
import { IAverageSpendPerPax } from "app/services/reporting/reporting.type";
import { saveAvgWeeklySpendStats, saveAvgMonthlySpendStats, saveTotalMonthlySpendStats, saveAvgPerPaxSpendStats, saveAvgPerSpendDurationStats } from "app/reducers/dashboardSlice";
import { bookingsService } from "app/services/booking.service";
import { GenerateRandomHexColorCode } from "./helper";
import { forEach } from "lodash-es";

/** Formats the date in 'YYYY-MM-DD' format */
export const formatDate = (selectedDate: Date) => Moment(selectedDate).format('YYYY-MM-DD');

/** Store the weekly spend average statistics */
export async function StoreAvgWeeklySpendStats(filterParameters: IFilterParameters): Promise<void> {
  const apiResult = await ReportingService.getWeeklySpendAverage(filterParameters);
  store.dispatch(saveAvgWeeklySpendStats(apiResult));
}

/** Store the monthly spend average statistics */
export async function StoreAvgMonthlySpendStats(filterParameters: IFilterParameters): Promise<void> {
  const apiResult = await ReportingService.getMonthlySpendAverage(filterParameters);
  store.dispatch(saveAvgMonthlySpendStats(apiResult));
}

/** Store the monthly spend total statistics */
export async function StoreTotalMonthlySpendStats(filterParameters: IFilterParameters): Promise<void> {
  const apiResult = await ReportingService.getMonthlySpendTotal(filterParameters);
  store.dispatch(saveTotalMonthlySpendStats(apiResult));
}

/** Store the per pax spend average statistics */
export async function StoreAvgPerPaxSpendStats(filterParameters: IFilterParameters): Promise<void> {

  let avgSpendList: IAverageSpendPerPax[] = [];
  const apiResult = await ReportingService.getPerPaxSpendAverage(filterParameters);

  if (apiResult.length > 0) {

    avgSpendList = apiResult.sort((x,y) => x.bookedPeople > y.bookedPeople ? 1 : -1).map((item) => {
      const element: IAverageSpendPerPax = {
        averageSpend: item.averageSpend,
        bookedPeople: item.bookedPeople,
        HexColorCode: GenerateRandomHexColorCode(),
        AverageSpendInDollar: parseFloat((item.averageSpend / 100).toFixed(2)),
      }

      return element;
    });
  }

  store.dispatch(saveAvgPerPaxSpendStats(avgSpendList));
}

/** Store the per duration spend average statistics */
export async function StoreAvgPerSpendDurationStats(filterParameters: IFilterParameters): Promise<void> {
  const apiResult = await ReportingService.getPerSpendDurationAverage(filterParameters);
  store.dispatch(saveAvgPerSpendDurationStats(apiResult));
}

export async function StoreBookingsInfoByFilters(filterParameters: IFilterParameters): Promise<void> {
  const bookings = await bookingsService.getBookings(filterParameters, true);
  store.dispatch(saveFilteredBookings(bookings));
}

/** Store the Service Tables by The Date */
export async function StoreServicesByTheDate(filterParameters: IFilterParameters): Promise<void> {
  let combinedTableLayoutsForAllVenues = [] as IVenueTablesLayoutsForTheDay[];
  await Promise.all(filterParameters.selectedVenues.map(async sVenue => {
    const venueFilters = {...filterParameters, selectedVenue: sVenue } as IFilterParameters;
    const tablesLayouts = await bookingsService.getTables(venueFilters);
    combinedTableLayoutsForAllVenues = [...combinedTableLayoutsForAllVenues, { venueId: sVenue.id, tablesLayoutsForTheDay: tablesLayouts}];
  }));
  
  store.dispatch(saveTablesByTheDate(combinedTableLayoutsForAllVenues));
}

/** Store the Schedule details by the Date */
export async function StoreSchedulesByTheDate(filterParameters: IFilterParameters): Promise<void> {
  let combinedSchedulesForAllVenues = [] as IVenueScheduleDetailsByTheDate[];
  await Promise.all(filterParameters.selectedVenues.map(async sVenue => {
    const venueFilters = {...filterParameters, selectedVenue: sVenue } as IFilterParameters;
    const schedule = await bookingsService.getSchedule(venueFilters);
    combinedSchedulesForAllVenues = [...combinedSchedulesForAllVenues, 
      { venueId: sVenue.id, scheduleDetailsByTheDate: schedule } as IVenueScheduleDetailsByTheDate];
  }));
  
  store.dispatch(saveScheduleByTheDate(combinedSchedulesForAllVenues));
}

/** Store the Bookings By The Date */
export async function StoreBookingsByTheDate(filterParameters: IFilterParameters): Promise<void> {
  const toDate = new Date(filterParameters.serviceGroupingPaxCountDate);
  toDate.setDate(toDate.getDate() + 1);
  const toDateFormattedStr = formatDate(toDate);
  let combinedBookingsForAllVenues = [] as IVenueBookings[];
  await Promise.all(filterParameters.selectedVenues.map(async sVenue => {
    const filterParamsForServiceTypeBookingsGroup = { 
      ...filterParameters, 
      fromDate: filterParameters.serviceGroupingPaxCountDate,
      toDate: toDateFormattedStr,
      selectedVenue: sVenue
    } as IFilterParameters;
  
    const bookings = await bookingsService.getBookings(filterParamsForServiceTypeBookingsGroup, false);
    combinedBookingsForAllVenues = [...combinedBookingsForAllVenues, 
      { venueId: sVenue.id, bookings: bookings } as IVenueBookings];
  }));
  
  store.dispatch(saveBookingsByTheDate(combinedBookingsForAllVenues));
}